"use client";

/**
 * The applied filters as removable chips above the results (owner,
 * 2026-10-29): every set filter is one solid chip with its value and an
 * x that removes just that filter. On phones the row scrolls sideways and
 * shows only when something is set (the list page's second row under the
 * search field); on desktop it sits under the pill bar.
 *
 * `appliedChips` is the one place that turns a filter URL into chips; the
 * empty-results suggestions use it to count "without this filter".
 */
import { useEffect, useMemo, useState } from "react";
import type { ReadonlyURLSearchParams } from "next/navigation";
import { X } from "lucide-react";
import { useLocale, useT } from "@/lib/locale-context";
import type { TranslationKey } from "@/lib/i18n";
import { cachedFilterNames, loadFilterNames, type FilterNames } from "@/lib/filter-names";
import { FACT_CHIP, MERGED_FACTS, isFactFilter } from "@/lib/text-facts";
import { kindChipKey, LISTING_KINDS } from "@/lib/property-kinds";
import { haptic } from "@/lib/haptics";
import { SELECTED } from "@/lib/ui-classes";

export interface AppliedChip {
  id: string;
  label: string;
  /** URL updates that undo this chip (null clears a key). */
  remove: Record<string, string | string[] | null>;
  /** The desktop bar shows this filter in a pill of its own (the deal,
   *  the kind, the place, the rooms, the price): no chip there. */
  pill?: boolean;
}

/** Whether the desktop bar has a pill that shows this chip's filter. */
function onDesktopPill(id: string, kind: string): boolean {
  const key = id.split(":")[0];
  if (["leased", "category", "listing_kind", "property_kind", "location_ids", "complex_slug", "street", "draw", "radius", "price"].includes(key)) return true;
  // A shop or a garage has the area pill where a home has the rooms;
  // a plot has its sot pill, a house its sot in the kind popover.
  if (key === "rooms") return kind !== "commercial" && kind !== "garage";
  if (key === "area") return kind === "commercial" || kind === "garage";
  if (key === "sot") return kind === "land" || kind === "house";
  return false;
}

type Params = URLSearchParams | ReadonlyURLSearchParams;
type T = (k: TranslationKey) => string;

const SOURCES: Record<string, string> = {
  bina_az: "bina.az", tap_az: "tap.az", lalafo_az: "lalafo.az", arenda_az: "arenda.az",
  yeniemlak_az: "yeniemlak.az", emlaktap_az: "emlaktap.az", ev10_az: "ev10.az", own: "menzil.ai",
};

const fmt = (v: string) => v.replace(/\B(?=(\d{3})+(?!\d))/g, " ");

/** A min / max pair as one short value ("60-90", "≥ 60", "≤ 90"). One end
 *  alone gets a sign, not a hyphen: "-90" read like a minus (owner,
 *  2026-10-31). The no-break space keeps the sign with its number. */
function range(a: string | null, b: string | null): string | null {
  if (!a && !b) return null;
  if (a && b) return a === b ? fmt(a) : `${fmt(a)}-${fmt(b)}`;
  return a ? `≥\u00a0${fmt(a)}` : `≤\u00a0${fmt(b!)}`;
}

/** Every filter the URL sets, as chips, in the order the sheet shows them. */
export function appliedChips(sp: Params, t: T, names: FilterNames | null, locale: string): AppliedChip[] {
  const out: AppliedChip[] = [];
  const get = (k: string) => sp.get(k) || null;
  const all = (k: string) => sp.getAll(k).filter(Boolean);
  const multi = (key: string, label: (v: string) => string | null) => {
    const vals = all(key);
    for (const v of vals) {
      const l = label(v);
      if (!l) continue;
      const rest = vals.filter((x) => x !== v);
      out.push({ id: `${key}:${v}`, label: l, remove: { [key]: rest.length ? rest : null } });
    }
  };
  const yn = (v: string | null) => (v === "yes" ? t("listings.filter.tri.yes") : v === "no" ? t("listings.filter.tri.no") : null);

  // The deal (sale is the default and no chip), the kind and the build.
  const leased = get("leased");
  if (leased === "true" || leased === "all") {
    out.push({ id: "leased", label: t(leased === "true" ? "listings.filter.transaction.rent" : "listings.filter.transaction.both"), remove: { leased: null, rent_period: null } });
  }
  const period = get("rent_period");
  if (period === "daily" || period === "room" || period === "all") {
    out.push({ id: "rent_period", label: t(`listings.filter.rent_period.${period}` as TranslationKey), remove: { rent_period: null } });
  }
  const category = get("category");
  if (category && (LISTING_KINDS as readonly string[]).includes(category)) {
    out.push({ id: "category", label: t(`listings.filter.category.${category}` as TranslationKey), remove: { category: null, property_kind: null } });
  }
  multi("property_kind", (v) => t(kindChipKey(v)));
  multi("listing_kind", (v) => (
    v === "new" ? t("listings.filter.building_type.new") : v === "old" ? t("listings.filter.building_type.old")
      : v === "house" ? t("listings.filter.category.house") : v === "land" ? t("listings.filter.category.land") : null
  ));
  const bt = get("building_type");
  if (bt === "new" || bt === "old") out.push({ id: "building_type", label: t(`listings.filter.building_type.${bt}` as TranslationKey), remove: { building_type: null } });

  // The place.
  multi("location_ids", (v) => names?.loc[v] ?? null);
  multi("complex_slug", (v) => names?.res[v] ?? null);
  multi("city", (v) => v);
  const district = get("district");
  if (district) out.push({ id: "district", label: district, remove: { district: null } });
  const streets = all("street");
  if (streets.length) out.push({ id: "street", label: streets.length > 1 ? `${t("listings.magic.chip.street")} (${streets.length})` : t("listings.magic.chip.street"), remove: { street: null } });
  if (get("draw")) out.push({ id: "draw", label: t("listings.applied.drawn"), remove: { draw: null } });
  if (get("center_lat") && get("radius_km")) {
    out.push({ id: "radius", label: t("listings.applied.radius").replace("{n}", get("radius_km")!), remove: { center_lat: null, center_lng: null, radius_km: null } });
  }
  // What the search rules out ("Masazır və Xırdalan olmasın", "kombi
  // olmasın"): a chip each, "Xırdalan xaric".
  const except = (name: string) => t("listings.applied.except").replace("{name}", name);
  multi("exclude_location_ids", (v) => (names?.loc[v] ? except(names.loc[v]) : null));
  multi("exclude_city", (v) => except(v));
  multi("exclude_tags", (v) => {
    const def = names?.vocab.tags.find((x) => x.id === v);
    return def ? except(def.labels[locale as keyof typeof def.labels] ?? def.labels.az) : null;
  });

  // Rooms, price, area: one chip each.
  const rooms = [...new Set(all("rooms").map(Number).filter((n) => !Number.isNaN(n)))].sort((a, b) => a - b);
  if (rooms.length) {
    const words = rooms.map((n) => (n >= 5 ? "5+" : String(n)));
    const contiguous = rooms.length > 2 && rooms.every((n, i) => i === 0 || n === rooms[i - 1] + 1);
    const n = contiguous ? `${words[0]}-${words[words.length - 1]}` : [...new Set(words)].join(", ");
    out.push({ id: "rooms", label: t("listings.magic.chip.rooms").replace("{n}", n), remove: { rooms: null } });
  }
  const price = range(get("price_min"), get("price_max"));
  if (price) out.push({ id: "price", label: `${price} AZN`, remove: { price_min: null, price_max: null } });
  const area = range(get("area_min"), get("area_max"));
  if (area) out.push({ id: "area", label: `${area} m²`, remove: { area_min: null, area_max: null } });
  const sot = range(get("sot_min"), get("sot_max"));
  if (sot) out.push({ id: "sot", label: `${sot} sot`, remove: { sot_min: null, sot_max: null } });
  const pps = range(get("pps_min"), get("pps_max"));
  if (pps) out.push({ id: "pps", label: `${pps} AZN/sot`, remove: { pps_min: null, pps_max: null } });
  const ppm = range(get("ppm_min"), get("ppm_max"));
  if (ppm) out.push({ id: "ppm", label: `${ppm} AZN/m²`, remove: { ppm_min: null, ppm_max: null } });

  // Repair and floor.
  const rep = yn(get("has_repair"));
  if (rep) out.push({ id: "has_repair", label: `${t("listings.filter.repair")}: ${rep}`, remove: { has_repair: null } });
  const floor = range(get("floor_min"), get("floor_max"));
  if (floor) out.push({ id: "floor", label: t("listings.magic.chip.floor_range").replace("{r}", floor), remove: { floor_min: null, floor_max: null } });
  if (get("exclude_first_floor") === "true") out.push({ id: "exclude_first_floor", label: `${t("listings.filter.floor")}: ${t("listings.filter.not_first")}`, remove: { exclude_first_floor: null } });
  if (get("exclude_last_floor") === "true") out.push({ id: "exclude_last_floor", label: `${t("listings.filter.floor")}: ${t("listings.filter.not_last")}`, remove: { exclude_last_floor: null } });
  // The number of floors as the sheet names it ("Mərtəbə sayı: 2+"): an
  // open range read badly as an adjective ("2+-этажный", "-5 mərtəbəli").
  const storeys = range(get("total_floors_min"), get("total_floors_max"));
  if (storeys) out.push({ id: "storeys", label: `${t("listings.filter.total_floors")}: ${storeys}`, remove: { total_floors_min: null, total_floors_max: null } });

  // The deed, then the sheet's topics in their order.
  const deed = yn(get("has_bill_of_sale"));
  if (deed) out.push({ id: "has_bill_of_sale", label: `${t("listings.filter.deed")}: ${deed}`, remove: { has_bill_of_sale: null } });
  // The mortgage is one question: "İpoteka ilə almaq olar: Bəli".
  const mortgageWant = get("has_mortgage");
  if (mortgageWant === "yes" || mortgageWant === "no") {
    out.push({ id: "has_mortgage", label: `${t("listings.fact.mortgage")}: ${t(mortgageWant === "yes" ? "listings.filter.yesno.yes" : "listings.filter.yesno.no")}`, remove: { has_mortgage: null } });
  }
  const gap = get("deed_gap");
  if (gap === "yes" || gap === "no") out.push({ id: "deed_gap", label: `${t("listings.filter.deed_gap")}: ${t(`listings.filter.deed_gap.${gap}` as TranslationKey)}`, remove: { deed_gap: null } });
  if (get("mortgage_kind") === "takeover") {
    out.push({ id: "mortgage_kind", label: t("listings.filter.mortgage_takeover"), remove: { mortgage_kind: null, max_initial: null, mortgage_rate_min: null, mortgage_rate_max: null, mortgage_terms_known: null, mortgage_years: null } });
  }
  // An old link's merged fact reads as its question's side.
  multi("fact", (v) => {
    const merged = MERGED_FACTS[v];
    if (merged?.key === "has_repair") return `${t("listings.filter.repair")}: ${t(merged.want === "yes" ? "listings.filter.tri.yes" : "listings.filter.tri.no")}`;
    if (merged?.key === "has_mortgage") return `${t("listings.fact.mortgage")}: ${t("listings.filter.yesno.yes")}`;
    return isFactFilter(v) ? t(FACT_CHIP[v]) : null;
  });
  multi("tags", (v) => {
    const def = names?.vocab.tags.find((x) => x.id === v);
    return def ? def.labels[locale as keyof typeof def.labels] ?? def.labels.az : null;
  });
  multi("source", (v) => SOURCES[v] ?? v);
  const seller = get("seller_type");
  if (seller === "owner" || seller === "agent" || seller === "company") {
    out.push({ id: "seller_type", label: `${t("listings.filter.seller_type")}: ${t(`listings.filter.seller_type.${seller}` as TranslationKey)}`, remove: { seller_type: null } });
  }
  multi("deal_tier", (v) => (["great", "good", "fair", "overpriced"].includes(v) ? t(`listings.filter.deal_tier.${v}` as TranslationKey) : null));
  const myield = get("min_yield");
  if (myield) out.push({ id: "min_yield", label: t("listings.filter.min_yield.chip").replace("{n}", myield), remove: { min_yield: null } });
  multi("developer", (v) => names?.dev[v] ?? null);
  const walk = get("metro_walk");
  if (walk) out.push({ id: "metro_walk", label: `${t("listings.filter.metro_walk")}: ${t("listings.filter.metro_walk.min").replace("{n}", walk)}`, remove: { metro_walk: null } });
  multi("photo_condition", (v) => t(`photointel.tier.${v}` as TranslationKey));
  multi("photo_has", (v) => t(`listings.filter.photo_has.${v}` as TranslationKey));
  const after = get("listed_after"), before = get("listed_before");
  if (after || before) out.push({ id: "listed", label: `${t("listings.filter.listed.title")}: ${[after, before].filter(Boolean).join(" - ")}`, remove: { listed_after: null, listed_before: null } });
  const viewed = get("viewed");
  if (viewed === "hide" || viewed === "only") out.push({ id: "viewed", label: t(viewed === "hide" ? "viewed.filter.hide" : "viewed.filter.only"), remove: { viewed: null } });
  const q = get("q");
  if (q) out.push({ id: "q", label: t("listings.magic.chip.q").replace("{v}", q), remove: { q: null } });
  // A residence amenity's evidence (the residence page's chip links).
  const amenity = get("amenity");
  if (amenity) out.push({ id: "amenity", label: t(`residence.amen.${amenity}` as TranslationKey), remove: { amenity: null } });
  if (get("photo")) out.push({ id: "photo", label: t("listings.magic.chip.photo"), remove: { photo: null, ...(get("sort") === "look" ? { sort: null } : {}) } });
  const kinds = all("listing_kind");
  const kind = category && (LISTING_KINDS as readonly string[]).includes(category) ? category
    : kinds.includes("land") ? "land" : kinds.includes("house") ? "house" : "";
  return out.map((c) => ({ ...c, pill: onDesktopPill(c.id, kind) }));
}

/** Keys whose chip needs a name only the reference lists know. */
const NAMED = ["location_ids", "complex_slug", "developer", "tags", "exclude_location_ids", "exclude_tags"];

export default function AppliedFilterChips({
  searchParams,
  onRemove,
  className = "",
}: {
  searchParams: Params;
  /** Apply one chip's removal (the page's mergeParams). The page's one
   *  clear-all is "Sıfırlayın" in the results row (owner, 2026-10-29). */
  onRemove: (updates: Record<string, string | string[] | null>) => void;
  className?: string;
}) {
  const t = useT();
  const { locale } = useLocale();
  const [names, setNames] = useState<FilterNames | null>(() => cachedFilterNames());
  const needsNames = NAMED.some((k) => searchParams.getAll(k).length > 0);
  useEffect(() => {
    if (needsNames && !names) loadFilterNames().then(setNames).catch(() => {});
  }, [needsNames, names]);
  const key = searchParams.toString();
  const chips = useMemo(
    () => appliedChips(searchParams, t, names, locale),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [key, names, locale, t],
  );
  if (!chips.length) return null;
  // Desktop shows a chip only for a filter with no pill of its own (owner,
  // 2026-10-29: the price read twice); phones keep them all, the row is
  // their only summary.
  const desktopNone = chips.every((c) => c.pill);
  return (
    <div className={`${className} ${desktopNone ? "sm:hidden" : ""}`}>
      <ul
        aria-label={t("listings.applied.label")}
        className="-mx-4 flex items-center gap-1.5 overflow-x-auto px-4 pb-0.5 [scrollbar-width:none] sm:mx-0 sm:flex-wrap sm:overflow-visible sm:px-0 [&::-webkit-scrollbar]:hidden"
      >
        {chips.map((c) => (
          <li key={c.id} className={`shrink-0 ${c.pill ? "sm:hidden" : ""}`}>
            {/* A set filter: the faint brand tint of the bar's pills
                (owner, 2026-10-31, option D). */}
            <span className={`inline-flex h-8 items-center gap-0.5 rounded-lg border pl-2.5 text-xs font-semibold ${SELECTED}`}>
              <span className="max-w-[14rem] truncate">{c.label}</span>
              <button
                type="button"
                onClick={() => { haptic(6); onRemove(c.remove); }}
                aria-label={t("listings.applied.remove").replace("{label}", c.label)}
                className="mr-0.5 inline-flex h-7 w-7 items-center justify-center rounded-md text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
              >
                <X className="h-3.5 w-3.5" aria-hidden />
              </button>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
